/*
 * <AtomLoader/> — Universal loading experience across every generate-and-wait moment.
 *
 * Source of truth: the founder-approved reference file
 * (emergent_atom_loader_greenlight_with_source.md, Feb 2026). Colors,
 * orbit geometry, electron motion path/durations, and nucleus pulse
 * match the reference EXACTLY — do not re-derive from prose.
 *
 * Design system:
 *   ATOM_COLORS.orbit    "#22324B"  (slate navy — orbit lines + text)
 *   ATOM_COLORS.nucleus  "#3EB489"  (mint — pulsing nucleus)
 *   ATOM_COLORS.electron "#8ADCC0"  (seafoam — orbiting electrons)
 *   ATOM_COLORS.muted    "#6C7A90"  (sublabel)
 *
 * A single-file color, animation, or message nudge changes every
 * loader in the app.
 *
 * Path A image-generation wait (later) consumes this same component:
 *   <AtomLoader message="Creating your lesson images"
 *               sublabel="This takes a few seconds the first time — then it's instant." />
 */
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

export const ATOM_COLORS = {
  orbit: "#22324B",
  nucleus: "#3EB489",
  electron: "#8ADCC0",
  ink: "#22324B",
  muted: "#6C7A90",
  card: "#FFFFFF",
};

// Founder-supplied single-message label (spec §4). Referenced only by the
// /dev/atom-loader preview — the Path A image-generation wait is not wired
// to a live surface yet. (Slide preview now uses the shared pool.)
export const ATOM_MESSAGES = {
  lessonImages: {
    message: "Creating your lesson images",
    sublabel: "This takes a few seconds the first time — then it's instant.",
  },
};

// ── Shared generator loader pool (Jun 2026) ────────────────────────────
// ONE stage-neutral message pool shown during EVERY generate-and-wait in
// the product. Stage-neutral so it can loop indefinitely on a long build
// without ever implying progress or regression. Strings are FINAL and
// verbatim per the founder spec — do NOT reword, extend, or add to the
// pool. Shuffled per generation and rotated via `useShuffledMessageCycle`.
export const GENERATOR_LOADER_MESSAGES = [
  "Lining the task up with what you picked.",
  "Building something you'd want to teach.",
  "Good lessons don't fall out of a machine. Give it a second.",
  "Wondering how you ever did this on a prep period.",
  "Resisting the urge to add a word search.",
  "Making something that will make your class think.",
  "Not padding it. You'd notice.",
  "Building the version you'd want if someone made it for you.",
];

// Rotation interval for the shared pool. Tune here — one constant, every
// surface picks it up.
export const LOADER_ROTATION_MS = 6000;

// After this long on a single build, a persistent reassurance line joins
// the rotating pool. It is NOT a progress claim and NOT a promise about
// what happens if the tab closes (that behaviour is unverified) — only an
// acknowledgement that bigger builds take longer.
export const LONG_BUILD_REASSURANCE_MS = 90000;
export const LONG_BUILD_REASSURANCE_TEXT =
  "Still working. Bigger builds take a little longer.";

const _KEYFRAMES_ID = "atom-loader-keyframes";

/** Inject keyframes exactly once. Reference file uses them via CSS,
 *  React equivalent is a mounted <style> block. */
function useAtomKeyframes() {
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (document.getElementById(_KEYFRAMES_ID)) return;
    const style = document.createElement("style");
    style.id = _KEYFRAMES_ID;
    style.textContent = `
      @keyframes atomNucleusPulse {
        0%, 100% { transform: scale(1); opacity: 0.9; }
        50% { transform: scale(1.32); opacity: 1; }
      }
      @keyframes atomDotBlink {
        0%, 20% { opacity: 0; }
        50% { opacity: 1; }
        100% { opacity: 0; }
      }
      .atom-loader-nucleus {
        transform-box: fill-box;
        transform-origin: center;
        filter: drop-shadow(0 0 5px rgba(62,180,137,.55));
        animation: atomNucleusPulse 2.3s ease-in-out infinite;
      }
      .atom-loader-electron {
        filter: drop-shadow(0 0 6px rgba(138,220,192,.85));
      }
      .atom-loader-dots span {
        animation: atomDotBlink 1.4s infinite both;
      }
      .atom-loader-dots span:nth-child(2) { animation-delay: 0.2s; }
      .atom-loader-dots span:nth-child(3) { animation-delay: 0.4s; }
      @keyframes atomLabelFade {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      .atom-loader-label-fade {
        animation: atomLabelFade 0.45s ease;
      }
      @media (prefers-reduced-motion: reduce) {
        .atom-loader-nucleus { animation: none; }
        .atom-loader-dots span { animation: none; opacity: 1; }
        .atom-loader-label-fade { animation: none; }
      }
    `;
    document.head.appendChild(style);
  }, []);
}

/** Fisher-Yates shuffle on a copy (never mutates the source pool). */
function _shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * Shuffled, indefinitely-looping message cycle for the shared generator
 * loader pool (`GENERATOR_LOADER_MESSAGES`).
 *
 *  · Shuffles the pool once per mount — i.e. per generation. Two
 *    consecutive generations get independent random orders.
 *  · Rotates one line at a time every `intervalMs` (default
 *    `LOADER_ROTATION_MS`).
 *  · Loops forever: when the order is exhausted it reshuffles and
 *    continues, so a four-minute build keeps cycling.
 *  · No line repeats until the pool is exhausted, and the reshuffle
 *    never places the same line back-to-back across the boundary.
 *  · Fade is a render concern (see `.atom-loader-label-fade`); this hook
 *    only advances the string. `prefers-reduced-motion` gates the fade,
 *    not the language.
 *
 *  Exported so surfaces with their own layout (e.g. the Math Engine
 *  `GenerationLoader` heartbeat) can drive the same cycled label without
 *  mounting the full AtomLoader card.
 */
export function useShuffledMessageCycle(messages, intervalMs = LOADER_ROTATION_MS) {
  const valid = Array.isArray(messages) && messages.length > 0;
  const orderRef = useRef([]);
  const idxRef = useRef(0);
  const [current, setCurrent] = useState("");
  useEffect(() => {
    if (!valid) {
      setCurrent("");
      return undefined;
    }
    // Fresh shuffle at the start of each generation (mount).
    orderRef.current = _shuffle(messages);
    idxRef.current = 0;
    setCurrent(orderRef.current[0]);
    if (messages.length <= 1) return undefined;
    const id = setInterval(() => {
      let next = idxRef.current + 1;
      if (next >= orderRef.current.length) {
        const last = orderRef.current[orderRef.current.length - 1];
        const reshuffled = _shuffle(messages);
        // Never repeat the last shown line back-to-back at the boundary.
        if (reshuffled.length > 1 && reshuffled[0] === last) {
          const j = 1 + Math.floor(Math.random() * (reshuffled.length - 1));
          [reshuffled[0], reshuffled[j]] = [reshuffled[j], reshuffled[0]];
        }
        orderRef.current = reshuffled;
        next = 0;
      }
      idxRef.current = next;
      setCurrent(orderRef.current[next]);
    }, intervalMs);
    return () => clearInterval(id);
  }, [messages, intervalMs, valid]);
  return current;
}

const SIZE_PX = {
  sm: { card: 260, atom: 100, label: 14, sub: 11.5 },
  md: { card: 360, atom: 150, label: 16, sub: 12.5 },
  // `lg` is used by the Math Engine's full-card generation loader —
  // atom bigger, orbits more visible from further back on the page.
  lg: { card: 440, atom: 190, label: 18, sub: 13.5 },
};

// Mobile floor — at very narrow viewports (< 380px) the card should
// never squeeze below this width, and the atom SVG should scale down
// proportionally. Prevents crush when AtomLoader sits inside a
// parent flex row on 320-390px devices.
const MOBILE_ATOM_PX = { sm: 84, md: 120, lg: 140 };

export const AtomLoader = ({
  message,
  messages,
  sublabel,
  size = "md",
  className = "",
  ...rest
}) => {
  useAtomKeyframes();
  const isCycling = Array.isArray(messages) && messages.length > 1;
  const cycled = useShuffledMessageCycle(messages, LOADER_ROTATION_MS);
  const label = (message || cycled || "").trim();
  const s = SIZE_PX[size] || SIZE_PX.md;
  const mobileAtom = MOBILE_ATOM_PX[size] || MOBILE_ATOM_PX.md;

  // Long-build reassurance: once a cycling build passes the threshold, a
  // persistent line joins the rotating pool. Only for real generations
  // (isCycling); static single-message loaders never show it.
  const [longBuild, setLongBuild] = useState(false);
  useEffect(() => {
    if (!isCycling) {
      setLongBuild(false);
      return undefined;
    }
    const id = setTimeout(() => setLongBuild(true), LONG_BUILD_REASSURANCE_MS);
    return () => clearTimeout(id);
  }, [isCycling]);

  // Reference file source-of-truth SVG. Replicated exactly.
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="atom-loader"
      className={`atom-loader-card atom-loader-card--${size} ${className}`.trim()}
      style={{
        // Below 640px: card fills the container and never overflows.
        // Above 640px: capped at design width. `min-width: 0` unlocks
        // the card so a parent flex row can shrink it (no crush /
        // horizontal-scroll on 320-390px devices).
        width: "100%",
        maxWidth: s.card,
        minWidth: 0,
        boxSizing: "border-box",
        background: ATOM_COLORS.card,
        border: "1px solid rgba(34,50,75,0.06)",
        borderRadius: 18,
        padding: size === "sm" ? "22px 20px 20px" : "28px 26px 24px",
        boxShadow:
          "0 18px 44px -18px rgba(34,50,75,0.28), 0 2px 6px rgba(34,50,75,0.05)",
        textAlign: "center",
        color: ATOM_COLORS.ink,
        fontFamily: "'Montserrat', system-ui, sans-serif",
      }}
      {...rest}
    >
      <svg
        className="atom-loader-atom"
        viewBox="0 0 200 200"
        aria-hidden="true"
        style={{
          // Responsive: shrink to `mobileAtom` on narrow screens,
          // grow to `s.atom` on ≥640px. Always centered block.
          width: `clamp(${mobileAtom}px, 32vw, ${s.atom}px)`,
          height: "auto",
          aspectRatio: "1 / 1",
          margin: "0 auto",
          display: "block",
        }}
      >
        {/* Two orbits, evenly spread (90 apart) */}
        <g transform="rotate(45 100 100)">
          <ellipse
            cx="100"
            cy="100"
            rx="84"
            ry="34"
            fill="none"
            stroke={ATOM_COLORS.orbit}
            strokeWidth="3.6"
            opacity="0.85"
          />
          <circle className="atom-loader-electron" r="6.5" fill={ATOM_COLORS.electron}>
            <animateMotion
              dur="3.0s"
              repeatCount="indefinite"
              path="M16,100 a84,34 0 1,0 168,0 a84,34 0 1,0 -168,0"
            />
          </circle>
        </g>
        <g transform="rotate(135 100 100)">
          <ellipse
            cx="100"
            cy="100"
            rx="84"
            ry="34"
            fill="none"
            stroke={ATOM_COLORS.orbit}
            strokeWidth="3.6"
            opacity="0.85"
          />
          <circle className="atom-loader-electron" r="6.5" fill={ATOM_COLORS.electron}>
            <animateMotion
              dur="3.9s"
              begin="-1.6s"
              repeatCount="indefinite"
              path="M16,100 a84,34 0 1,0 168,0 a84,34 0 1,0 -168,0"
            />
          </circle>
        </g>
        {/* Pulsing mint nucleus (drawn last, on top) */}
        <circle
          className="atom-loader-nucleus"
          cx="100"
          cy="100"
          r="9"
          fill={ATOM_COLORS.nucleus}
        />
      </svg>

      <p
        data-testid="atom-loader-label"
        style={{
          margin: "22px 0 4px",
          fontSize: s.label,
          fontWeight: 600,
          letterSpacing: "0.1px",
          color: ATOM_COLORS.orbit,
        }}
      >
        {isCycling ? (
          <span key={label} className="atom-loader-label-fade">
            {label}
          </span>
        ) : (
          label
        )}
        <span className="atom-loader-dots" aria-hidden="true">
          <span>.</span>
          <span>.</span>
          <span>.</span>
        </span>
      </p>

      {sublabel ? (
        <p
          data-testid="atom-loader-sublabel"
          style={{
            margin: 0,
            fontSize: s.sub,
            fontWeight: 500,
            color: ATOM_COLORS.muted,
            letterSpacing: "0.2px",
          }}
        >
          {sublabel}
        </p>
      ) : null}

      {longBuild ? (
        <p
          data-testid="atom-loader-reassurance"
          aria-live="polite"
          style={{
            margin: sublabel ? "8px 0 0" : "6px 0 0",
            fontSize: s.sub,
            fontWeight: 600,
            color: ATOM_COLORS.muted,
            letterSpacing: "0.2px",
          }}
        >
          {LONG_BUILD_REASSURANCE_TEXT}
        </p>
      ) : null}
    </div>
  );
};

/**
 * <AtomSpinner/> — Just the animated atom SVG, no card, no message.
 *
 * Use this inside surfaces that already provide their own card /
 * headline / body copy but still want the branded atom in the
 * spinner slot. The Math Engine `GenerationLoader` uses it so the
 * rich stage-copy preamble stays intact while the wait-icon becomes
 * the branded atom instead of a generic lucide spinner.
 *
 * `size` mirrors the standard AtomLoader sizes and controls only the
 * SVG width (there's no card padding to scale).
 */
export const AtomSpinner = ({ size = "md", className = "", ...rest }) => {
  useAtomKeyframes();
  const s = SIZE_PX[size] || SIZE_PX.md;
  const mobileAtom = MOBILE_ATOM_PX[size] || MOBILE_ATOM_PX.md;
  return (
    <svg
      role="img"
      aria-label="Loading"
      data-testid="atom-spinner"
      className={`atom-loader-atom ${className}`.trim()}
      viewBox="0 0 200 200"
      style={{
        width: `clamp(${mobileAtom}px, 32vw, ${s.atom}px)`,
        height: "auto",
        aspectRatio: "1 / 1",
        margin: "0 auto",
        display: "block",
      }}
      {...rest}
    >
      <g transform="rotate(45 100 100)">
        <ellipse
          cx="100"
          cy="100"
          rx="84"
          ry="34"
          fill="none"
          stroke={ATOM_COLORS.orbit}
          strokeWidth="3.6"
          opacity="0.85"
        />
        <circle className="atom-loader-electron" r="6.5" fill={ATOM_COLORS.electron}>
          <animateMotion
            dur="3.0s"
            repeatCount="indefinite"
            path="M16,100 a84,34 0 1,0 168,0 a84,34 0 1,0 -168,0"
          />
        </circle>
      </g>
      <g transform="rotate(135 100 100)">
        <ellipse
          cx="100"
          cy="100"
          rx="84"
          ry="34"
          fill="none"
          stroke={ATOM_COLORS.orbit}
          strokeWidth="3.6"
          opacity="0.85"
        />
        <circle className="atom-loader-electron" r="6.5" fill={ATOM_COLORS.electron}>
          <animateMotion
            dur="3.9s"
            begin="-1.6s"
            repeatCount="indefinite"
            path="M16,100 a84,34 0 1,0 168,0 a84,34 0 1,0 -168,0"
          />
        </circle>
      </g>
      <circle
        className="atom-loader-nucleus"
        cx="100"
        cy="100"
        r="9"
        fill={ATOM_COLORS.nucleus}
      />
    </svg>
  );
};

AtomSpinner.propTypes = {
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  className: PropTypes.string,
};

AtomLoader.propTypes = {
  message: PropTypes.string,
  messages: PropTypes.arrayOf(PropTypes.string),
  sublabel: PropTypes.string,
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  className: PropTypes.string,
};

export default AtomLoader;
